import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Button,
    Form,
    Spinner,
    Alert,
    Dropdown,
} from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';

import { PlayingCard } from '../games/cardComponent.tsx'; // Adjust path as needed
import { School, SCHOOL_NAMES } from '../../util/cardEffects/effectTypes.ts'; // Adjust path as needed
import { createText } from '../../util/cardEffects/cardText.ts';
import { isEffectType } from '../../util/cardEffects/effectProcessing.tsx';
import { api } from '../../index.tsx';
import { ExpandToFitContainer } from './ExpandToFitContainer.tsx';

type DeckData = {
    deck_id: number | null;
    deck_name: string;
    owner_id: number | null;
    username?: string;
    public: boolean;
}

export default function ViewDeck() {
    const { deckId } = useParams<{ deckId: string }>();
    const MAX_NON_CLASS_CARDS = 40
    const MIN_NON_CLASS_CARDS = 40;
    const [deckData, setDeckData] = useState<DeckData>({
        deck_id: null,
        deck_name: '',
        owner_id: null,
        public: false,
    });
    const [cardList, setCardList] = useState<{ card_id: number; count: number; class_card: boolean }[]>([]);
    const [allCards, setAllCards] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);

    // Search/filter state (from CardManagementPanel)
    const [searchTerm, setSearchTerm] = useState('');
    const [searchCost, setSearchCost] = useState('');
    const [searchSchool, setSearchSchool] = useState<string | null>(null);
    const [searchClass, setSearchClass] = useState(false);
    const [searchRitual, setSearchRitual] = useState(false);

    const [showCopiedToast, setShowCopiedToast] = useState(false);

    const updateCardsInDeck = useCallback(async () => {
        try {
            const [deckRes, cardsRes] = await Promise.all([
                api.post('/decks/get', { deckId }, { withCredentials: true }),
                api.post('/decks/cards/get', { deckId }, { withCredentials: true }),
            ]);
            setDeckData(deckRes.data);
            setCardList(cardsRes.data.sort((c: any) => c.class_card ? 0 : 1));
        } catch {
            window.location.href = '/logout';
        } finally {
            setLoading(false);
        }
    }, [deckId]);

    const getAllCards = async () => {
        try {
            const res = await api.post('/cards/get', {}, { withCredentials: true });
            const sorted = res.data.sort((a: any, b: any) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            );
            setAllCards(sorted);
        } catch {
            setErr('Failed to load all cards.');
        }
    };

    useEffect(() => {
        updateCardsInDeck();
    }, [deckId, updateCardsInDeck]);

    useEffect(() => {
        getAllCards();
    }, []);

    const addCardToDeck = async (card_id: number, max: number) => {
        const inDeck = cardList.find((c) => c.card_id === card_id)?.count ?? 0;
        if (inDeck >= max) return;
        try {
            await api.post('/decks/cards/add', { deckId, cardId: card_id }, { withCredentials: true });
            await updateCardsInDeck();
        } catch {
            setErr('Failed to add card.');
        }
    };

    const removeCardFromDeck = async (card_id: number) => {
        try {
            await api.post('/decks/cards/remove', { deckId, cardId: card_id }, { withCredentials: true });
            await updateCardsInDeck();
        } catch {
            setErr('Failed to remove card.');
        }
    };

    // Filtered card list (from CardManagementPanel)
    const filteredAllCards = useMemo(() => {
        return allCards.filter((card) => {
            const matchesNameOrId =
                card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                String(card.card_id) === searchTerm;
            const matchesSchool = searchSchool === null || card.school === searchSchool;
            const matchesCost = searchCost === '' || String(card.cost) === searchCost;
            const matchesClass = (card.class_card !== null && card.class_card !== false) || !searchClass
            const matchesRitual = (isEffectType("ritual", card.play_ability ? (card.play_ability) : null)
                || isEffectType("ritual", card.cast_ability ? (card.cast_ability) : null)
                || isEffectType("ritual", card.sustain_ability ? (card.sustain_ability) : null)
                || isEffectType("ritual", card.passive_ability ? (card.passive_ability) : null)) || !searchRitual
            return matchesNameOrId && matchesSchool && matchesCost && matchesClass && matchesRitual;
        }).sort((a, b) => a.cost - b.cost).sort((a, b) => a.school > b.school ? 1 : -1);
    }, [allCards, searchTerm, searchCost, searchSchool, searchClass, searchRitual]);

    const classCardInDeck = cardList.find((c) => {
        const card = allCards.find((ac) => ac.card_id === c.card_id);
        return card?.class_card;
    });
    const classCardCount = classCardInDeck?.count ?? 0;
    const nonClassCount = cardList.reduce((sum, c) => sum + (c.count || 0), 0) - classCardCount;

    const deckValid = nonClassCount >= MIN_NON_CLASS_CARDS && classCardCount <= 1;
    const deckColor = !deckValid ? 'text-danger' : nonClassCount > MAX_NON_CLASS_CARDS ? 'text-warning' : 'text-success';

    // Helper to build PlayingCard props from card data
    const buildCardProps = (card: any) => ({
        name: card.name,
        cost: card.cost,
        castThreshold: card.cast_threshold,
        focus: card.cost,
        tempFocus: 0,
        cast: card.cast_ability ? createText(card.cast_ability) : null,
        play: card.play_ability ? createText(card.play_ability) : null,
        sustain: card.sustain_ability ? createText(card.sustain_ability) : null,
        passive: card.passive_ability ? createText(card.passive_ability) : null,
        backgroundImage: card.background_image || null,
        school: card.school as School,
        class_card: card.class_card,
        energizeAmount: card.energize_amount,
        scale: 0.8, // Smaller scale for grid display
        faceDown: false,
        highlightOnFullFocus: false,
        forceNoHightlight: true,
        verticalIndex: 0,
        scaleOverride: 1,
    });



    return (
        <Container className="my-4 pt-2 pb-2">
            {showCopiedToast && (
                <div className="toast show position-fixed top-0 end-0 m-3" style={{ zIndex: 1050 }}>
                    <div className="toast-body">Copied</div>
                </div>
            )}
            {loading ? (
                <Row className="justify-content-center">
                    <Spinner animation="border" role="status" />
                </Row>
            ) :
                <>
                    {err ? <Alert variant="danger" className="mb-3">{err}</Alert> : ""}
                    <Row className="align-items-center mb-3">
                        <Col as="h2" className="mb-0">
                            <DeckName disabled={!!deckData.username} deckName={deckData.deck_name + (deckData.username ?
                                ` (${deckData.username})` :
                                "")} deckId={String(deckData.deck_id)} />{
                                deckData.deck_name ?
                                    ""
                                    : 'Invalid Deck Id'
                            }
                        </Col>
                        <Col className="text-end">
                            {deckData.username && <Link
                                to={""}
                                onClick={async () => {
                                    setShowCopiedToast(true);
                                    setTimeout(() => setShowCopiedToast(false), 2000);
                                    await api.post('/decks/copy', { deckId }, { withCredentials: true });
                                }}
                                style={{ marginRight: "10px", marginLeft: "10px" }}
                                className="btn btn-outline-secondary btn-sm me-2"
                            >
                                Copy
                            </Link>}
                            <Link to="/decks" className="btn btn-outline-secondary btn-sm me-2">Back to Decks</Link>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col xs={12} className="text-end">
                            <span className={`fw-semibold ${deckColor}`}>{!deckData.username ? <>Publicly visible: <input
                                type="checkbox"
                                checked={deckData.public}
                                onChange={async () => {
                                    setDeckData({ ...deckData, public: !deckData.public });
                                    await api.post('/decks/set/visible', { deckId, visible: !deckData.public }, { withCredentials: true });
                                }}
                                className="h-4 w-4 cursor-pointer appearance-none rounded border border-gray-300 bg-white checked:border-green-500 checked:bg-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                            /> • </> : <></>}Non-class: {nonClassCount} / {MAX_NON_CLASS_CARDS}{` • Class: ${classCardCount}/1`}</span>
                        </Col>
                    </Row>

                    <Row className="g-3 align-items-stretch">
                        <Col xs={12}>
                            <Card className="mb-3">
                                <Card.Body>
                                    <Card.Title className="mb-3">In Deck</Card.Title>
                                    {cardList.length === 0 && deckData.deck_name ? (
                                        <Alert variant="info" className="mb-0">
                                            This deck is empty. Add cards to get started.
                                        </Alert>
                                    ) : (
                                        <Row className="g-3">
                                            {cardList.map((item) => {
                                                const cardInfo = allCards.find((c) => c.card_id === item.card_id);
                                                if (!cardInfo) return null;

                                                return (
                                                    <Col key={item.card_id} xs={6} sm={4} md={3} lg={2} xl={2}>
                                                        <div className="position-relative">
                                                            <ExpandToFitContainer>
                                                                <PlayingCard {...buildCardProps(cardInfo)}
                                                                    classCard={cardInfo.class_card}
                                                                    ritual={isEffectType("ritual", cardInfo.play_ability)
                                                                        || isEffectType("ritual", cardInfo.cast_ability)
                                                                        || isEffectType("ritual", cardInfo.sustain_ability)
                                                                        || isEffectType("ritual", cardInfo.passive_ability)
                                                                    }
                                                                />
                                                            </ExpandToFitContainer>
                                                            <div
                                                                className="position-absolute top-0 end-0 m-1 d-flex flex-column gap-1"
                                                                style={{ zIndex: 100 }}
                                                            >
                                                                <span className="badge bg-dark">×{item.count}/{cardInfo.max_count}</span>
                                                                {!deckData.username ? <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    className="py-0 px-1"
                                                                    style={{ fontSize: '0.7rem' }}
                                                                    onClick={() => removeCardFromDeck(item.card_id)}
                                                                >
                                                                    −
                                                                </Button> : ""}
                                                                {item.count < cardInfo.max_count && !item.class_card && !deckData.username && nonClassCount < MAX_NON_CLASS_CARDS && (
                                                                    <Button
                                                                        variant="primary"
                                                                        size="sm"
                                                                        className="py-0 px-1"
                                                                        style={{ fontSize: '0.7rem' }}
                                                                        onClick={() => addCardToDeck(item.card_id, cardInfo.max_count)}
                                                                    >
                                                                        +
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* All Cards with Search/Filter */}
                    {deckData.deck_name && !deckData.username ?
                        <Row className="mb-3">
                            <Col xs={12}>
                                <Card className="mb-3">
                                    <Card.Body>
                                        <Card.Title className="mb-3">All Cards</Card.Title>

                                        {/* Search/Filter Controls (from CardManagementPanel) */}
                                        <Row className="g-2 mb-3">
                                            <Col md={3}>
                                                <Form.Control
                                                    placeholder="Search by name or ID"
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </Col>
                                            <Col md={3}>
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Cost"
                                                    value={searchCost}
                                                    onChange={(e) => setSearchCost(e.target.value)}
                                                />
                                            </Col>
                                            <Col md={3}>
                                                <Dropdown onSelect={(val) => setSearchSchool(val)}>
                                                    <Dropdown.Toggle variant="outline-secondary" className="w-100">
                                                        {searchSchool || 'All Schools'}
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu className="w-100">
                                                        <Dropdown.Item eventKey={undefined}>All</Dropdown.Item>
                                                        {SCHOOL_NAMES.map((school) => (
                                                            <Dropdown.Item key={school} eventKey={school}>
                                                                {school}
                                                            </Dropdown.Item>
                                                        ))}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </Col>
                                            <Col md={1}>
                                                <Form.Check
                                                    label={"class"}
                                                    style={{ marginTop: "10px" }}
                                                    checked={searchClass}
                                                    onChange={(e) => {
                                                        setSearchClass(!searchClass);
                                                    }}
                                                />
                                            </Col>
                                            <Col md={1}>
                                                <Form.Check
                                                    label={"ritual"}
                                                    style={{ marginTop: "10px" }}
                                                    checked={searchRitual}
                                                    onChange={(e) => {
                                                        setSearchRitual(!searchRitual);
                                                    }}
                                                />
                                            </Col>
                                        </Row>

                                        <Card.Text className="text-muted mb-2">
                                            Click a card to add it to the deck. Showing {filteredAllCards.length} of {allCards.length} cards.
                                        </Card.Text>

                                        {filteredAllCards.length === 0 ? (
                                            <Alert variant="info">No cards match your search criteria.</Alert>
                                        ) : (
                                            <Row className="g-3">
                                                {filteredAllCards.map((card) => {
                                                    const inDeckCount = cardList.find((x) => x.card_id === card.card_id)?.count ?? 0;
                                                    const isClassCard = card.class_card;
                                                    const wouldExceedClassLimit = isClassCard && classCardCount >= 1;
                                                    const wouldExceedNonClassLimit = !isClassCard && nonClassCount >= MAX_NON_CLASS_CARDS;
                                                    const disabled = inDeckCount >= card.max_count || wouldExceedClassLimit || wouldExceedNonClassLimit;

                                                    return (
                                                        <Col key={card.card_id} xs={6} sm={4} md={3} lg={2} xl={2}>
                                                            <div
                                                                className={`position-relative ${disabled ? 'opacity-50' : ''}`}
                                                                style={{ cursor: disabled || deckData.username ? 'not-allowed' : 'pointer' }}
                                                                onClick={() => !disabled && !deckData.username && addCardToDeck(card.card_id, card.max_count)
                                                                    // this will fail and throw an error anyway, but.... let's prevent that from even showing in the first place
                                                                }
                                                            >
                                                                <ExpandToFitContainer>
                                                                    <PlayingCard {...buildCardProps(card)}
                                                                        classCard={card.class_card}
                                                                        ritual={isEffectType("ritual", card.play_ability)
                                                                            || isEffectType("ritual", card.cast_ability)
                                                                            || isEffectType("ritual", card.sustain_ability)
                                                                            || isEffectType("ritual", card.passive_ability)
                                                                        }
                                                                    />
                                                                </ExpandToFitContainer>
                                                                {inDeckCount > 0 && (
                                                                    <div
                                                                        className="position-absolute top-0 end-0 m-1"
                                                                        style={{ zIndex: 100 }}
                                                                    >
                                                                        <span className="badge bg-success">In deck: {inDeckCount}/{card.max_count}</span>
                                                                    </div>
                                                                )}
                                                                {inDeckCount >= card.max_count && (
                                                                    <div
                                                                        className="position-absolute end-0 m-1"
                                                                        style={{ zIndex: 100, top: 25 }}
                                                                    >
                                                                        <span className="badge bg-secondary">Max copies</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row> :
                        ""
                    }
                </>
            }
        </Container>
    );
}

function DeckName(props: { deckName: string, deckId: string, disabled: boolean }) {
    const [deckName, setDeckName] = useState(props.deckName);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleChange = (value: string) => {
        setDeckName(value);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            if (props.deckId !== "")
                api.post('/decks/set/name', { deckId: props.deckId, name: value }, { withCredentials: true });
        }, 300);
    };

    return (
        <Form.Control
            disabled={props.disabled}
            value={deckName}
            onChange={(e) => {
                handleChange(e.target.value);
            }}
            style={{
                fontSize: "1.5em",
                backgroundColor: "lightgray",
                border: "none",
            }}
            className="deck-name-input"
        />
    );
}

